import { AntDesign } from "@expo/vector-icons";
import { useFocusEffect } from "expo-router";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, Text, Pressable } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated";
import { useDispatch } from "react-redux";

import Subtitle from "@/components/Subtitle";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import InputCrypto from "@/components/exchange/InputCrypto";
import InputUsd from "@/components/exchange/InputUsd";
import TimerExchange from "@/components/exchange/TimerExchange";
import Input from "@/components/input/Input";
import ExchangeModal from "@/components/modal/banking/ExchangeModal";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import i18n from "@/locales/i18n";
import { runAbortableAsync, sendRequest } from "@/utils/ApiUtil";
import { fiatTypeAccounts } from "@/utils/accounts/Utils";
import { checkAssetPrecision } from "@/utils/transactions/ExchangeCurrencies";

type FormData = {
  amount: string;
  description: string;
};

interface createExchange {
  sourceAccountId: string;
  destinationAccountId: string;
  amount: number;
  action: string;
  description?: string;
}

export default function Exchange() {
  const { t } = useTranslation();
  const globalValues = useAppSelector((state: any) => state);
  const dispatch = useDispatch();

  const [remainingTime, setRemainingTime] = useState(10);
  const [currency, setCurrency] = useState("");
  const [availableBalance, setAvailableBalance] = useState(0);
  const [destinationAvailableBalance, setDestinationAvailableBalance] =
    useState(0);
  const [amount, setAmount] = useState(0);
  const [currencyValueConverted, setCurrencyValueConverted] = useState(0);
  const [currencyToConvert, setCurrencyToConvert] = useState("");
  const [accountIdFiat, setAccountIdFiat] = useState("");
  const [accountIdCrypto, setAccountIdCrypto] = useState("");
  const [accountCryptoPrecision, setAccountCryptoPrecision] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [exchangeId, setExchangeId] = useState("");
  const [exchangeDate, setExchangeDate] = useState("");
  const [isScreenFocused, setIsScreenFocused] = useState(false);
  const [fee, setFee] = useState(0);
  const [rate, setRate] = useState(0);
  const [isRateFetched, setIsRateFetched] = useState(false);
  const [isRateFetching, setIsRateFetching] = useState(false);
  const [isFiatSource, setIsFiatSource] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [blockExchange, setBlockExchange] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  //Used to check if the user have a crypto account opened
  const [hasCryptoAccount, setHasCryptoAccount] = useState(true);

  const abortControllerRef = useRef<AbortController | null>(null);
  const amountRef = useRef<number>(0);

  const rotate = useSharedValue(0);

  const { control, getValues } = useForm<FormData>({
    mode: "onSubmit",
  });

  function resetStates() {
    setAmount(0);
    setCurrencyValueConverted(0);
    setRate(0);
    setFee(0);
    setRemainingTime(10);
    setAvailableBalance(0);
    setDestinationAvailableBalance(0);
    setIsRateFetched(false);
    setIsFiatSource(true);
    setIsRateFetching(false);
    setIsSubmitted(false);
    setBlockExchange(false);
    setHasCryptoAccount(true);
    setErrorMessage("");
    setCurrency("");
    setCurrencyToConvert("");
    setAccountIdFiat("");
    setAccountIdCrypto("");
    rotate.value = 0;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }

  useFocusEffect(
    React.useCallback(() => {
      setIsScreenFocused(true);
      dispatch(setForm({ currentMenuItem: t("home/home:option.exchange") }));
      resetStates();

      const account = globalValues.accountsData?.find(
        (account: any) =>
          fiatTypeAccounts.includes(account.assetTypeId) &&
          account.status === "OPEN"
      );

      const assetDestinationDefault = globalValues.accountsData?.find(
        (account: any) =>
          !fiatTypeAccounts.includes(account.assetTypeId) &&
          account.status === "OPEN"
      );

      setCurrency(account?.assetTypeId ?? "");
      setFee(account?.feeStructure?.conversionFee ?? 0);
      setAccountIdFiat(account?.id ?? "");

      if (
        assetDestinationDefault &&
        assetDestinationDefault.assetTypeId &&
        assetDestinationDefault.id
      ) {
        setCurrencyToConvert(assetDestinationDefault.assetTypeId);
        setAccountIdCrypto(assetDestinationDefault.id);
        setAccountCryptoPrecision(
          checkAssetPrecision(assetDestinationDefault.assetTypeId)
        );
      } else {
        setHasCryptoAccount(false);
      }

      return () => {
        setIsScreenFocused(false);
        resetStates();
      };
    }, [])
  );

  useEffect(() => {
    if (
      !isRateFetched ||
      !hasCryptoAccount ||
      isRateFetching ||
      isSubmitted ||
      blockExchange ||
      amount === 0
    ) {
      return;
    }

    const timer = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime > 0) {
          return prevTime - 1;
        } else {
          if (isScreenFocused && amount > 0 && !blockExchange) {
            if (amount !== amountRef.current) {
              setIsRateFetched(false);
              setIsRateFetching(false);
              setExchangeId("");
              return 10;
            } else {
              setIsRateFetched(false);
              setExchangeId("");
              createExchange(amount);
              return 10;
            }
          }
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [
    isRateFetched,
    isRateFetching,
    isScreenFocused,
    isSubmitted,
    amount,
    blockExchange,
  ]);

  useEffect(() => {
    const account = globalValues.accountsData?.find(
      (objeto) => objeto.id === accountIdFiat
    );

    const assetDestinationDefault = globalValues.accountsData?.find(
      (objeto) => objeto.id === accountIdCrypto
    );

    if (account && assetDestinationDefault) {
      if (isFiatSource) {
        setAvailableBalance(account?.availableBalance ?? 0);
        setFee(account?.feeStructure?.conversionFee ?? 0);
        setDestinationAvailableBalance(
          assetDestinationDefault.availableBalance ?? 0
        );
      } else {
        setAvailableBalance(assetDestinationDefault.availableBalance ?? 0);
        setFee(assetDestinationDefault?.feeStructure?.conversionFee ?? 0);
        setDestinationAvailableBalance(account?.availableBalance ?? 0);
      }
    }

    if (amount > availableBalance) {
      setBlockExchange(true);
      setErrorMessage(t("home/exchange:insufficientfunds"));
    } else if (amount > 499999 || currencyValueConverted > 499999) {
      setBlockExchange(true);
      setErrorMessage(t("home/exchange:above500k"));
    } else {
      setBlockExchange(false);
      setErrorMessage("");
    }
  }, [
    accountIdFiat,
    accountIdCrypto,
    isFiatSource,
    amount,
    currencyValueConverted,
    globalValues.accountsData,
  ]);

  async function createExchange(value: number, destinationId?: string) {
    setIsRateFetching(true);
    const abortControllerExchange = new AbortController();
    abortControllerRef.current = abortControllerExchange;

    const destinationAccountId = destinationId
      ? destinationId
      : isFiatSource
        ? accountIdCrypto
        : accountIdFiat;

    const data = {
      sourceAccountId: isFiatSource ? accountIdFiat : accountIdCrypto,
      destinationAccountId,
      amount: value,
      action: "FIX_SOURCE",
    } as createExchange;

    if (getValues("description")) {
      data.description = getValues("description");
    }
    try {
      const exchange = await runAbortableAsync(
        () => sendRequest("POST", "/exchange", data, true),
        abortControllerExchange.signal
      );
      if (exchange) {
        amountRef.current = value;
        setExchangeId(exchange.id ?? "");
        setCurrencyValueConverted(
          exchange.destinationDetails?.amountToCredit ?? ""
        );
        setExchangeDate(exchange.createdTimestamp ?? "");
        setRate(exchange.exchangeRate ?? 0);
        setIsRateFetched(true);
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setIsRateFetching(false);
    }
  }

  const minutes = Math.floor(remainingTime / 60);
  const seconds = remainingTime % 60;

  const rotateSyncIcon = () => {
    rotate.value = withTiming(rotate.value + 180, {
      duration: 1000,
    });
  };

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ rotate: `${rotate.value}deg` }],
    };
  });

  return (
    <Animated.View className="flex-1 bg-white1 items-center overflow-y-auto p-4 lg:p-0">
      <Title text={t("home/exchange:title")} />
      <View className="my-2">
        <Subtitle text={t("home/exchange:subtitle")} />
      </View>
      <View className="items-center p-4 my-2 lg:border lg:border-lightgray lg:rounded-lg lg:w-136 lg:shadow">
        <View className="z-[70]">
          {isFiatSource ? (
            <View className="z-50">
              <InputUsd
                accountIdFiat={accountIdFiat}
                currencyFiat={currency}
                availableBalance={availableBalance}
                valueConverted={currencyValueConverted}
                data={globalValues.accountsData}
                createExchange={(value) => createExchange(value)}
                setAmount={(value) => {
                  setAmount(value);
                  setRemainingTime(10);
                  setIsRateFetched(false);
                  setCurrencyValueConverted(0);
                }}
                setAccountIdFiat={(id) => {
                  setAccountIdFiat(id);
                  setAmount(0);
                  setRate(0);
                  setCurrencyValueConverted(0);
                }}
                blocked={!isFiatSource}
                isRateFetching={isRateFetching}
              />
            </View>
          ) : (
            <View className="z-50">
              <InputCrypto
                data={globalValues.accountsData}
                valueConverted={currencyValueConverted}
                currencyToConvert={currencyToConvert}
                availableBalance={availableBalance}
                createExchange={(value) => createExchange(value)}
                setAmount={(value) => {
                  setAmount(value);
                  setRemainingTime(10);
                  setIsRateFetched(false);
                  setCurrencyValueConverted(0);
                }}
                setAccountIdCrypto={(id) => {
                  setAccountIdCrypto(id);
                  setRate(0);
                  setCurrencyValueConverted(0);
                }}
                setCurrencyToConvert={(value) => setCurrencyToConvert(value)}
                precision={accountCryptoPrecision}
                blocked={!isFiatSource}
                isRateFetching={isRateFetching}
              />
            </View>
          )}
          <View className="z-40 absolute bottom-0 left-4 w-80 flex-col">
            <Text className="text-green">{`${t(
              "home/exchange:availablebalance"
            )}: ${
              isFiatSource
                ? new Intl.NumberFormat(i18n.language, {
                    style: "currency",
                    currency: "USD",
                  }).format(availableBalance)
                : availableBalance
            }`}</Text>
          </View>
        </View>
        <View style={{ height: 4 }} className="w-full items-center py-1 pr-2">
          <Text className="font-light text-red text-sm">{errorMessage}</Text>
        </View>

        {/* Sync icon to switch between fiat and crypto accounts */}
        <Animated.View className="mb-4 mt-6 items-center" style={animatedStyle}>
          <Pressable
            onPress={() => {
              setIsFiatSource(!isFiatSource);
              setAmount(0);
              setRate(0);
              setCurrencyValueConverted(0);
              setIsRateFetched(false);
              setIsRateFetching(false);
              setRemainingTime(10);
              abortControllerRef.current?.abort();
              abortControllerRef.current = null;
              rotateSyncIcon();
            }}
          >
            <AntDesign name="sync" size={32} className="text-black1" />
          </Pressable>
        </Animated.View>

        <View className="z-[60]">
          {isFiatSource ? (
            <InputCrypto
              data={globalValues.accountsData}
              valueConverted={currencyValueConverted}
              currencyToConvert={currencyToConvert}
              setAccountIdCrypto={(id) => {
                setAccountIdCrypto(id);
                setRate(0);
                setCurrencyValueConverted(0);
              }}
              setCurrencyToConvert={(value) => setCurrencyToConvert(value)}
              precision={accountCryptoPrecision}
              blocked={!isFiatSource}
              isRateFetching={isRateFetching}
            />
          ) : (
            <InputUsd
              accountIdFiat={accountIdFiat}
              currencyFiat={currency}
              valueConverted={currencyValueConverted}
              data={globalValues.accountsData}
              setAccountIdFiat={(id) => {
                setAccountIdFiat(id);
                setRate(0);
                setCurrencyValueConverted(0);
              }}
              blocked={!isFiatSource}
              isRateFetching={isRateFetching}
            />
          )}
          <View className="absolute bottom-0 left-4">
            <Text className="text-green">{`${t(
              "home/exchange:availablebalance"
            )}: ${
              isFiatSource
                ? destinationAvailableBalance
                : new Intl.NumberFormat(i18n.language, {
                    style: "currency",
                    currency: "USD",
                  }).format(destinationAvailableBalance)
            }`}</Text>
          </View>
        </View>
        <View className="mt-4">
          <Input
            name="description"
            control={control}
            label={t("home/exchange:description")}
            optional
          />
        </View>
        <View className="w-full items-center border-b border-midgray mb-1 p-0.5">
          {!hasCryptoAccount ? (
            <Text className="text-base font-normal text-red text-center">
              {t("home/exchange:advice")}
            </Text>
          ) : (
            <TimerExchange
              minutes={minutes}
              seconds={seconds}
              isOperationInProgress={isRateFetching}
            />
          )}
        </View>
        <View className="w-full items-center p-2">
          <View className="w-full justify-between items-center flex-row">
            <Text className="font-thin text-base">
              {t("home/exchange:fees.crypto")}:
            </Text>
            <Text className="text-base">{`${fee * 100}%`}</Text>
          </View>
          <View className="w-full justify-between items-center flex-row">
            <Text className="font-thin text-base">
              {t("home/exchange:fees.rate")}:
            </Text>
            <Text className="text-base">
              {new Intl.NumberFormat(i18n.language, {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 4,
              }).format(rate)}
            </Text>
          </View>
          <View className="w-full justify-between items-center flex-row">
            <Text className="font-thin text-base">
              {t("home/exchange:amounttodebit")}:
            </Text>
            <Text className="text-base">
              {isFiatSource
                ? new Intl.NumberFormat(i18n.language, {
                    style: "currency",
                    currency: "USD",
                  }).format(amount)
                : amount}
            </Text>
          </View>
          <View className="w-full justify-between items-center flex-row">
            <Text className="font-thin text-base">
              {t("home/exchange:amounttocredit")}:
            </Text>
            <Text className="text-base">
              {isFiatSource
                ? currencyValueConverted
                : new Intl.NumberFormat(i18n.language, {
                    style: "currency",
                    currency: "USD",
                  }).format(currencyValueConverted)}
            </Text>
          </View>
        </View>
      </View>
      <View className="w-full items-center">
        <Button
          label={
            isRateFetched
              ? t("common:button.review")
              : t("common:button.createexchange")
          }
          onClick={() =>
            isRateFetched ? setIsModalVisible(true) : createExchange(amount)
          }
          blocked={
            isRateFetched
              ? currencyValueConverted <= 0 ||
                amount <= 0 ||
                blockExchange ||
                exchangeId === "" ||
                isRateFetching
              : amount <= 0 ||
                !amount ||
                blockExchange ||
                !hasCryptoAccount ||
                isRateFetching
          }
        />
      </View>
      <ExchangeModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onClick={() => {
          setAmount(0);
          setCurrencyValueConverted(0);
          setIsModalVisible(false);
        }}
        onAccept={() => {
          setIsSubmitted(true);
          setRemainingTime(10);
        }}
        id={exchangeId}
        sourceAsset={isFiatSource ? currency : currencyToConvert}
        destinationAsset={isFiatSource ? currencyToConvert : currency}
        amountToDebit={amount}
        amountToCredit={currencyValueConverted}
        date={exchangeDate}
        fee={fee}
        rate={rate}
        timeRemaining={remainingTime}
      />
    </Animated.View>
  );
}
